// copied from git@bitbucket.org:reckonhq/apsworkflow-ui.git Code/src/utils/date-utils.ts

import {
  addDays,
  addMonths,
  differenceInCalendarDays,
  endOfMonth,
  format,
  formatDistance,
  formatDistanceStrict,
  getDate,
  isLastDayOfMonth,
  isSameMonth,
  isSameYear,
  isToday,
  isTomorrow,
  isYesterday,
  parseJSON,
  startOfMonth,
} from 'date-fns';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';

import { ISODate } from '../../src/components/common/date-picker-field';
import {
  RecurrenceFrequencyEnum,
  RecurrenceFrequencyEnumValue,
} from '../components/job/types/recurrence-frequency-enum';

function formatDateShort(date: Date | null | undefined): string {
  if (date) {
    return format(date, 'd MMM, yyyy');
  } else {
    return '';
  }
}

function formatFromDate(fromDate: Date, toDate: Date): string {
  // Utility to format the "from" date in a date range
  let formattedFromDate;
  if (isSameMonth(fromDate, toDate)) {
    // If both dates are also in the same month, format dates like this:
    // 15 - 16 May 2001
    formattedFromDate = format(fromDate, 'd');
  } else if (isSameYear(fromDate, toDate)) {
    // If both dates are for the same year, format dates like this:
    // 15 May - 15 Jun 2001
    formattedFromDate = format(fromDate, 'd MMM');
  } else {
    formattedFromDate = formatDateShort(fromDate);
  }
  return formattedFromDate;
}

function formatDatePair(fromDate: Date, toDate: Date): string {
  const formattedFromDate = formatFromDate(fromDate, toDate);
  const formattedToDate = formatDateShort(toDate);
  return `${formattedFromDate} – ${formattedToDate}`;
}

export const DateUtils = {
  estimateHoursToNumber(value: string): number {
    const cleanString = value.replace(/,/g, '').replace('h', '');
    if (cleanString.length) return parseFloat(cleanString);
    else return 0;
  },
  formatDateShort,
  formatFromDate,
  formatDatePair,
  formatDateTimeShort(date: Date | null | undefined): string {
    if (date) {
      return format(date, 'd MMM, yyyy · h:mma');
    } else {
      return '';
    }
  },
  formatButtonText(firstDate: Date, secondDate: Date): string {
    if (firstDate.getTime() === secondDate.getTime()) {
      return this.formatDateShort(firstDate);
    } else if (firstDate.getMonth() === secondDate.getMonth()) {
      return getDate(firstDate) + ' - ' + this.formatDateShort(secondDate);
    } else {
      return (
        this.formatDateShort(firstDate) +
        ' - ' +
        this.formatDateShort(secondDate)
      );
    }
  },
  formatDueDays(
    dueDate: Date,
    overdueSuffix: string = 'late',
    underduePrefix: string = ''
  ) {
    const now = new Date();

    if (isToday(dueDate)) {
      return 'today';
    } else if (isYesterday(dueDate)) {
      return 'yesterday';
    } else if (isTomorrow(dueDate)) {
      return 'tomorrow';
    } else {
      const daysDifference = formatDistanceStrict(dueDate, now, {
        unit: 'day',
        roundingMethod: dueDate < now ? 'floor' : 'ceil',
      });
      if (dueDate < now && overdueSuffix) {
        return daysDifference + ' ' + overdueSuffix;
      } else if (dueDate >= now && underduePrefix) {
        return underduePrefix + ' ' + daysDifference;
      } else {
        return daysDifference;
      }
    }
  },
  formatDistance(
    actionDate: Date,
    options?: {
      includeSeconds?: boolean;
      addSuffix?: boolean;
    }
  ) {
    // see https://redgum.atlassian.net/browse/RKNMAN-76?focusedCommentId=89465 for the rationale behind this special formatDistance
    // tl;dr the date-fns formatDistance (and distanceToNow) are too hard nosed when it comes to the cutover points
    // you can have two times that are 30 seconds apart and it'll change the number of days ago it shows
    // which looks weird
    // and we wanted more human terms in here for yesterday and tomorrow as well
    const now = new Date();

    if (isToday(actionDate)) {
      return formatDistance(actionDate, now, options);
    } else if (isYesterday(actionDate)) {
      return 'yesterday';
    } else if (isTomorrow(actionDate)) {
      return 'tomorrow';
    } else {
      const difInCalendarDays = Math.abs(
        differenceInCalendarDays(actionDate, now)
      );
      if (difInCalendarDays <= 30) {
        const addSuffix = options && options.addSuffix;
        const prefix = addSuffix && actionDate > now ? 'in ' : '';
        const suffix = addSuffix && actionDate < now ? ' ago' : '';
        return `${prefix}${difInCalendarDays} days${suffix}`;
      } else {
        return formatDistance(actionDate, now, options);
      }
    }
  },
};

export function formatDistanceOrDate(
  actionDate: Date,
  options?: {
    includeSeconds?: boolean;
    addSuffix?: boolean;
  },
  useDistanceIfCalendarDaysAgoLessThan: number = 7
) {
  const now = new Date();
  const difInCalendarDays = Math.abs(differenceInCalendarDays(actionDate, now));
  if (difInCalendarDays < useDistanceIfCalendarDaysAgoLessThan) {
    return DateUtils.formatDistance(actionDate, options);
  } else {
    return DateUtils.formatDateShort(actionDate);
  }
}

export function safeParseJSON(value: any) {
  if (!value) {
    return null;
  }
  if (typeof value === 'string') {
    return parseJSON(value);
  }
  if (typeof value === 'number') {
    return parseJSON(new Date(value).toISOString());
  }
  if (value instanceof Date) {
    return parseJSON(value.toISOString());
  }
  return null;
}

export function safeAddDays(value: number | Date | null, amount: number) {
  if (value === null) {
    return value;
  }
  return addDays(value, amount);
}

const RecurrenceFrequency = RecurrenceFrequencyEnum.valuesEnum;

// the "map" of enum to increment functions
const recurrenceFrequencyIncrementFunctions: Record<
  RecurrenceFrequencyEnumValue,
  (date: Date) => Date
> = {
  [RecurrenceFrequency.SevenDays]: (value) => addDays(value, 7),
  [RecurrenceFrequency.FourteenDays]: (value) => addDays(value, 14),
  [RecurrenceFrequency.OneMonth]: (value) => smartAddMonthsInternal(value, 1),
  [RecurrenceFrequency.TwoMonths]: (value) => smartAddMonthsInternal(value, 2),
  [RecurrenceFrequency.ThreeMonths]: (value) =>
    smartAddMonthsInternal(value, 3),
  [RecurrenceFrequency.SixMonths]: (value) => smartAddMonthsInternal(value, 6),
  [RecurrenceFrequency.TwelveMonths]: (value) =>
    smartAddMonthsInternal(value, 12),
};

export function incrementDate(
  startDate: Date | null | undefined,
  by: RecurrenceFrequencyEnumValue
) {
  if (!startDate) {
    return startDate;
  }

  return recurrenceFrequencyIncrementFunctions[by](startDate);
}

function smartAddMonthsInternal(startDate: Date, monthsToAdd: number) {
  // Strange-ish human oriented results required from this one.
  // Sounds simple on the tin - add X months to a date.
  // But, humans being humans, have come up with inconsistent number of days per month.
  // And, humans being humans, complain when you tell them that 31 Jan + 1 month is 2 March.
  // And, just for something even more interesting, this particular bunch of humans that this system targets
  // will expect 30 June + 1 month to be 31 July, because the input
  // is the end of the month so the output better be the end of the month

  const startIsLastDayOfMonth = isLastDayOfMonth(startDate);
  const rawResult = addMonths(startDate, monthsToAdd);
  const resultIsLastDayOfMonth = isLastDayOfMonth(rawResult);
  const startMonthNumber = startDate.getMonth();
  const desiredEndMonthNumber = (startMonthNumber + (monthsToAdd % 12)) % 12;
  const actualEndMonthNumber = rawResult.getMonth();
  const correctMonth = desiredEndMonthNumber === actualEndMonthNumber;

  let result: Date;

  if (!startIsLastDayOfMonth && correctMonth) {
    // Something simple like 1 Jan + 1 month, we hit 1 Feb
    result = rawResult;
  } else if (!startIsLastDayOfMonth && !correctMonth) {
    // 30 Jan + 1 month. That probably goes 30 Feb, which becomes 2 March on a normal year, 1 March on leap year
    // go back to the start of the month we rolled over into, then subtract one day,
    // that'll get us to the end of the previous month
    result = addDays(startOfMonth(rawResult), -1);
  } else if (startIsLastDayOfMonth && correctMonth && resultIsLastDayOfMonth) {
    // 31 July + 1 month comes out at 31 Aug. Woo!
    result = rawResult;
  } else if (startIsLastDayOfMonth && correctMonth && !resultIsLastDayOfMonth) {
    // 30 June + 1 month comes out as 30 July, which is not the last day of the month
    // just skip to the end of the month that we found
    result = endOfMonth(rawResult);
  } else if (startIsLastDayOfMonth && !correctMonth) {
    // don't think we need to differentiate if we hit the end of the month or not
    // I think the only way this can happen is something like 31 Jan + 1 month
    // which is exactly the same as !startislastdayofmonth above (30 jan + 1 month)
    // so we _could_ combine these two clauses
    // but I'm going to leave them separate
    result = addDays(startOfMonth(rawResult), -1);
  } else {
    // this, as far as I can tell, cannot ever be hit. We've covered all the cases above
    // but the compiler thinks that result is returned before being assigned
    // so we'll do a dumb assignment here just to make it be quiet
    result = rawResult;
  }

  return result;
}

export function smartAddMonths(
  startDate: Date | undefined | null,
  monthsToAdd: number
) {
  if (!startDate) {
    return startDate;
  }

  return smartAddMonthsInternal(startDate, monthsToAdd);
}

// timzone conversions lifted from old app src\processes\date-time-transformer.ts
// P01032 DateTime Transformer
export const DEFAULT_TIMEZONE = 'Australia/Melbourne';

/** This one is for sending a picked date TO the server */
// differs from the original vue implementation in that it accepts an ISODate (which comes from date-field)
export function localDateToTenantToUtc(
  localDate: Date | ISODate | null | undefined,
  tenantTimezone: string | undefined | null
) {
  // we'd rather not get a string out of this
  if (localDate === '') {
    return null;
  }
  if (localDate === null) {
    return null;
  }
  if (localDate === undefined) {
    return undefined;
  }

  const timezone = tenantTimezone || DEFAULT_TIMEZONE;
  // so, what we have coming in is a value, probably selected from a datepicker
  // the important bit is the date portion of it
  // something like 2020-05-08
  // Format the incoming date back to a string, but only if it's not already a string, and
  // tack on some dummy time as well. Unsure if it is truly needed, but I think it gets the point across better
  const localAsISODateString =
    localDate instanceof Date ? format(localDate, 'yyyy-MM-dd') : localDate;
  // the space in the string seems to be important, lifted straight from the example code for the library
  // https://github.com/marnusw/date-fns-tz#zonedtimetoutc
  const localAsString = localAsISODateString + ' 00:00:00.000';

  // then, pretending like it is the tenant timezone, conver that to UTC
  // at this point it doesn't matter what timezone the current computer is in, we've wiped that information out
  // so we can pretend that we are wherever we want - the second parameter is "timezone converting from"
  const result = fromZonedTime(localAsString, timezone);
  // I think we need to return a date here. I'd much rather return a full ISO string, but server wants a date
  return result;
}

/** This one is for transforming values that come FROM the server, for cases where you need a Date */
// differs from the original in that it also accepts a string and does the parsjson
export function utcToTenantToLocalDate(
  utcDateAndTime: string | Date | null | undefined,
  tenantTimezone: string | undefined | null
) {
  // we absolutely do not want a string coming out of this. null/undefined are OK. String is not
  if (utcDateAndTime === '') {
    return null;
  }
  if (utcDateAndTime === null) {
    return null;
  }
  if (utcDateAndTime === undefined) {
    return undefined;
  }

  // fair chance the incoming value has come directly from graphql, which gives us a string
  const parsed = parseJSON(
    typeof utcDateAndTime === 'string'
      ? utcDateAndTime
      : utcDateAndTime.toISOString()
  );
  const timezone = tenantTimezone || DEFAULT_TIMEZONE;
  // what we have is UTC representation of midnight in the tenants timezone
  // the time is important here as well

  // get it back to at least the tenants timezone
  const tenantDateAndTime = toZonedTime(parsed, timezone);
  // and then...
  // just pluck the date portion of it off?
  // if we were going to the server, we would have gone just the date portion of the local date -> to utc, pretending this was the tenant timezone
  // so when coming back, I think we can just rip the date bit of it out and pretend like it's a new local date

  // return a date so it is consistent with the sister localDateToTenantToUtc method?
  // the use of getYear getMonth etc give me the willies here, I'd rather format to string then parse again, but lets try this
  const resultAsDate = new Date(
    tenantDateAndTime.getFullYear(),
    tenantDateAndTime.getMonth(),
    tenantDateAndTime.getDate()
  );

  // alternatively, could do it as a string
  // const resultAsString = format(tenantDateAndTime, 'yyyy-MM-dd');

  return resultAsDate;
}
